import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Badge } from "@/components/ui/badge";
import { findEnumName } from "@/lib/enum-utils";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import { ApplicationStatus, type ApplicationListItemDTO } from "@/orval/model";
import {
  getOrganizationAdminApplicationGetApplicationsQueryKey,
  getOrganizationAdminApplicationGetApplicationsQueryOptions,
  organizationAdminApplicationGetApplications,
} from "@/orval/organization-admin-application";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { createFileRoute } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { PortalPageHeader } from "@/components/portal-page-header";

function getStatusClassName(status: ApplicationStatus) {
  if (status === ApplicationStatus.Approved) {
    return "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50 border-0";
  }
  if (status === ApplicationStatus.Rejected) {
    return "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50 border-0";
  }
  return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border-0";
}

const columns: CustomColumnDef<ApplicationListItemDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    accessorFn: (row) => `${row.primaryParentFirstName} ${row.primaryParentLastName}`,
    id: "name",
    sortField: "User.FullName",
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    accessorKey: "primaryParentEmail",
    sortField: "User.Email",
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Phone" />,
    accessorKey: "primaryParentPhone",
    sortField: "User.PhoneNumber",
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date Applied" />,
    accessorFn: (row) => format(parseISO(row.createdAt), "MM/dd/yyyy"),
    id: "createdAt",
    sortField: "createdAt",
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    accessorKey: "status",
    cell: ({ row }) => {
      const status = row.original.status;

      return <Badge className={getStatusClassName(status)}>{findEnumName(ApplicationStatus, status)}</Badge>;
    },
  },
  {
    header: "# Parents",
    accessorKey: "parentCount",
  },
  {
    header: "# Children",
    accessorKey: "childrenCount",
  },
];

const OrganizationAdminApplicationsPage = () => {
  const { organizationId } = Route.useParams();

  const getApplications = organizationAdminApplicationGetApplications;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Applications"
        subtitle={<span className="print:text-black">Manage registration applications for your organization</span>}
      />

      <DataTable
        columns={columns}
        queryFn={(params, signal) => getApplications(organizationId, params, signal)}
        queryKeyFn={(params) => getOrganizationAdminApplicationGetApplicationsQueryKey(organizationId, params)}
        rowLink={(row) => ({
          to: "/organizations/$organizationId/admin/applications/$applicationId",
          params: { organizationId, applicationId: row.id },
        })}
        getRowIdFn={(row) => row.id.toString()}
      />
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/applications")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);
    return ensureRouteQueryData(
      loaderContext,
      getOrganizationAdminApplicationGetApplicationsQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions({ searchString }),
      ),
    );
  },
  component: OrganizationAdminApplicationsPage,
});
